<script setup lang="ts">
import { computed, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import AppSidebar from './AppSidebar.vue';
import AppTopbar from './AppTopbar.vue';
import AICopilotSheet from '@/components/copilot/AICopilotSheet.vue';
import CopilotFab from '@/components/copilot/CopilotFab.vue';
import WelcomeGuide from '@/components/welcome/WelcomeGuide.vue';
import { useAuthStore } from '@/stores/auth';
import { useUiStore } from '@/stores/uiStore';
import { cn } from '@/lib/cn';

const route = useRoute();
const auth = useAuthStore();
const ui = useUiStore();

/**
 * Auto-open the Welcome Guide once per user (per-browser). We key off
 * the authenticated user id (or the dev shim user id when a real session
 * isn't established), and persist the "seen" set via `uiStore`. Users
 * can always re-open the guide from the profile dropdown.
 */
onMounted(() => {
  const key = auth.user?.id ?? auth.devShim?.userId;
  if (!key) return;
  if (ui.hasSeenWelcomeGuide(key)) return;
  ui.openWelcomeGuide();
});

/**
 * Routes opting into a wide editor layout drop the 1280px content cap and
 * the generous block padding — they need every pixel for split-pane work.
 * Sidebar + topbar still render so Copilot, Developer mode, and breadcrumbs
 * remain reachable.
 */
const wide = computed(() => Boolean(route.meta?.wide));
const mainClasses = computed(() =>
  cn(
    'flex-1 w-full',
    wide.value ? 'mx-0 max-w-none' : 'px-4 py-6 sm:px-6 sm:py-8 md:px-10 md:py-10 max-w-[1280px] mx-auto',
  ),
);
</script>

<template>
  <div class="min-h-screen bg-bg flex">
    <AppSidebar />
    <div class="flex-1 flex flex-col min-w-0">
      <AppTopbar />
      <main :class="mainClasses">
        <Transition name="route" mode="out-in" appear>
          <div :key="route.fullPath" class="w-full">
            <slot />
          </div>
        </Transition>
      </main>
    </div>
    <CopilotFab />
    <AICopilotSheet />
    <WelcomeGuide />
  </div>
</template>

<style scoped>
.route-enter-active {
  transition: opacity 220ms cubic-bezier(0.32, 0.72, 0, 1),
              transform 220ms cubic-bezier(0.32, 0.72, 0, 1);
}
.route-leave-active {
  transition: opacity 120ms cubic-bezier(0.32, 0.72, 0, 1);
}
.route-enter-from {
  opacity: 0;
  transform: translateY(6px);
}
.route-leave-to {
  opacity: 0;
}

@media (prefers-reduced-motion: reduce) {
  .route-enter-active,
  .route-leave-active {
    transition: none;
  }
  .route-enter-from {
    transform: none;
  }
}
</style>
